// ---------------------------------------------------------------------------
// Saving a Sales Rep, and the rep a connect needs (R18b, from pages/settings.ts)
//
//   - Save sends the profile: POST for a new rep, PUT for one that exists. On
//     ok the editor closes and the lists reload; a refusal keeps it open and
//     says the server's words.
//   - Connecting an account from the editor of a new rep needs a rep to assign
//     it to. The rep is created only once the account is connected (the
//     resolver of settings-accounts.ts runs then), so a sign-in the person
//     abandons leaves no rep behind, and the popup opens as fast as before.
//   - Cancel removes a rep created that way when it still holds no account,
//     so no rep without an account is left behind.
//
// Deleting a rep asks first; that is R18a's (settings-safeguards.ts).
// ---------------------------------------------------------------------------

import type { SalesRep } from '../wizard/types.js';
import { type Say, type Send, viaFetch } from './settings-flow.js';

/** What the editor asks before an account can be connected for a new rep. */
export const NAME_FIRST = 'Enter a name before connecting an account.';

/** Whether the profile in the editor has no name yet, so no account can be connected for it. */
export const nameMissing = (rep: Partial<SalesRep>) => !rep.name?.trim();

export interface SaveRepDeps {
  send?: Send;
  say: Say;
  setSaving(saving: boolean): void;
  /** The box above the panels is cleared when a save starts. */
  clearError(): void;
  /** The save went through: close the editor and forget the rep created for a connect. */
  saved(): void;
  reload(): Promise<void>;
}

/** Save sends the profile in the editor. */
export async function saveSalesRep(rep: Partial<SalesRep>, deps: SaveRepDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  deps.setSaving(true);
  deps.clearError();
  try {
    const isNew = !rep._id;
    const url = isNew ? '/api/ai-employees' : `/api/ai-employees/${encodeURIComponent(rep._id!)}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await send(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rep) });
    const data = await res.json();
    if (data.ok) {
      deps.say.success(isNew ? 'Sales Representative created.' : 'Sales Representative updated.');
      deps.saved();
      void deps.reload();
    } else {
      deps.say.error(data.error ?? 'Failed to save Sales Representative');
    }
  } catch {
    deps.say.error('Failed to save Sales Representative');
  }
  deps.setSaving(false);
}

export interface CreateRepDeps {
  send?: Send;
  say: Say;
  /** The rep exists now: the editor holds its id, and it is remembered as created for the connect. */
  created(id: string): void;
  reload(): Promise<void>;
}

/**
 * The id of the rep in the editor, creating it first when it is new, so a
 * just-connected account can be assigned to it. Null when there is no editor,
 * no name, or the server refused.
 */
export async function repForConnect(rep: Partial<SalesRep> | null, deps: CreateRepDeps): Promise<string | null> {
  const send = deps.send ?? viaFetch;
  if (!rep) return null;
  if (rep._id) return rep._id;
  if (nameMissing(rep)) {
    deps.say.error(NAME_FIRST);
    return null;
  }
  try {
    const res = await send('/api/ai-employees', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rep),
    });
    const data = await res.json();
    if (data.ok && data.aiEmployee?._id) {
      const id = data.aiEmployee._id as string;
      deps.created(id);
      await deps.reload();
      return id;
    }
    deps.say.error(data.error ?? 'Failed to create Sales Representative');
    return null;
  } catch {
    deps.say.error('Failed to create Sales Representative');
    return null;
  }
}

export interface OrphanDeps {
  send?: Send;
  reload(): Promise<void>;
}

/**
 * After Cancel: removes the rep created for a connect when it holds no
 * account, best effort. `orphanId` is that rep, or null when none was created.
 */
export async function removeOrphanRep(orphanId: string | null, holdsAccount: boolean, deps: OrphanDeps): Promise<void> {
  const send = deps.send ?? viaFetch;
  if (!orphanId || holdsAccount) return;
  try {
    await send(`/api/ai-employees/${encodeURIComponent(orphanId)}`, { method: 'DELETE' });
    await deps.reload();
  } catch {
    /* best-effort cleanup */
  }
}
